import Phaser from "phaser";
import { COLORS } from "../../../../ui/colors";
import { DailyChallengeClientData } from "../../../../models/daily-challenge";
import { drawWidgetBackground, QUEST_WIDGET_W } from "./widget-background";

const WIDGET_H = 84;
// Always shows the last (fanciest) tier icon in the reward-ladder spritesheet as a
// generic decorative prize icon, rather than the specific icon for today's day.
const REWARD_ICON_FRAME = 6;
// Native frame size of the weekly-rewards spritesheet (see sprite-loader.ts) — scaled
// by an integer factor so the pixel art stays crisp instead of being stretched to an
// arbitrary size.
const REWARD_ICON_NATIVE_SIZE = 64;
const REWARD_ICON_SCALE = 1;

/**
 * DailyChallengeWidget: compact widget showing the player's personal 7-day streak —
 * complete today's kill target to advance it and unlock that day's credit reward.
 * Usage: new DailyChallengeWidget(scene, x, y, data)
 */
export class DailyChallengeWidget extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x: number, y: number, data: DailyChallengeClientData) {
    super(scene, x, y);

    // bgX matches the other quest widgets' inset convention so left edges line up.
    const bgX = 8;
    const bgY = -16;
    const bg = scene.add.graphics();
    drawWidgetBackground(bg, bgX, bgY, QUEST_WIDGET_W, WIDGET_H);
    this.add(bg);

    // Title row: "DAILY CHALLENGE" (yellow) immediately followed by "- X/7 STREAK" (white).
    const titleY = bgY + 12;
    const titleLabel = scene.add.text(bgX + 22, titleY, "DAILY CHALLENGE", {
      fontSize: "15px",
      color: COLORS.YELLOW,
      fontFamily: "Anton",
      fontStyle: "bold",
    });
    this.add(titleLabel);

    const streakLabel = scene.add.text(bgX + 22 + titleLabel.width + 6, titleY, `- ${data.streakCount}/7 STREAK`, {
      fontSize: "15px",
      color: COLORS.WHITE,
      fontFamily: "Anton",
    });
    this.add(streakLabel);

    // Reward icon — right-aligned, below the title row; text sits to its left.
    const iconSize = REWARD_ICON_NATIVE_SIZE * REWARD_ICON_SCALE;
    const iconX = bgX + QUEST_WIDGET_W - 20 - iconSize / 2;
    const rowY = titleY + 18 + iconSize / 2;
    const rewardIcon = scene.add.sprite(iconX, rowY-24, "weekly-rewards", REWARD_ICON_FRAME)
      .setScale(REWARD_ICON_SCALE)
      .setOrigin(0.5);
    this.add(rewardIcon);

    const rewardCredits = data.rewardLadder[data.today.day - 1]?.credits ?? 0;
    // Overlaid on the reward icon itself, near its bottom edge — a stroke keeps it
    // readable over whatever icon art is underneath.
    this.add(
      scene.add.text(iconX, rowY + iconSize / 2 - 32, `+${rewardCredits} CREDITS`, {
        fontSize: "12px", color: COLORS.WHITE, fontFamily: "Anton", fontStyle: "bold",
        stroke: "#000000", strokeThickness: 3,
      }).setOrigin(0.5),
    );

    const textX = bgX + 22;
    const textMaxWidth = iconX - iconSize / 2 - 12 - textX;

    if (data.today.completedToday) {
      this.add(
        scene.add.text(textX, rowY, "DONE\nCome back tomorrow", {
          fontSize: "12px", color: "#4ade80", fontFamily: "Anton", fontStyle: "bold",
          wordWrap: { width: textMaxWidth },
        }).setOrigin(0, 0.5),
      );
      return;
    }

    this.add(
      scene.add.text(textX, rowY - 6, `Kill ${data.today.killTarget} ${data.today.monster} (${data.today.sessionKills}/${data.today.killTarget})`, {
        fontSize: "16px", color: COLORS.WHITE, fontFamily: "Anton", fontStyle: "bold",
        wordWrap: { width: textMaxWidth },
      }).setOrigin(0, 0.5),
    );
  }
}
